import { useState } from 'react';
import { customInstance } from '@edison/api-client-react';
import { AlertCircle, CheckCircle2, TriangleAlert, Info, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button.js';

// Mirror of the server ApportionmentView (services/overhead-apportionment.ts). Kept as a local
// interface — this is the response contract of GET/PUT /api/expenditure/apportionment.
export interface ApportionmentView {
  mode: 'manual' | 'computed' | 'custom';
  customPercent: number | null;
  customReason: string | null;
  effectiveDefaultPercent: number | null;
  computed: {
    percent: number | null;
    rdLabour: number;
    payrollTotal: number;
    incomeYear: string;
    claimMode: string;
    monthsCovered: number;
    // apportionment claims only — the labour-hours basis (EY advice): percent = R&D hours ÷ total hours
    basis?: 'labour_hours';
    labourHours?: {
      percent: number | null;
      rdHours: number;
      totalHours: number;
      people: Array<{ userId: string; name: string; employmentType: 'salaried' | 'casual'; standardHours: number; rdHours: number }>;
      excludedUnrated: Array<{ userId: string; name: string }>;
      missingAnnualHours: Array<{ userId: string; name: string }>;
    };
    payrollPercent?: number | null; // cross-check: R&D labour $ ÷ Xero loaded payroll
  };
  // GET only, labour-hours basis only: how many claimable FY lines carry the current percent.
  lineStatus?: LabourPctLineStatus;
}

export interface LabourPctLineStatus {
  claimableLines: number;
  onLabourPct: number;
  onLabourPctCurrent: number;
  stale: Array<{ percent: number; lines: number }>;
  notOnLabourPct: number;
}

interface ApplyLabourPctResult {
  dryRun: boolean;
  scope: 'all_claimable' | 'labour_pct_only';
  percent: number;
  matched: number;
  updated: number;
  totals: { amount: number; claimableBefore: number; claimableAfter: number; claimableDelta: number };
}

const fmtHours = (n: number) => n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtAUD2 = (n: number) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 }).format(n);

function formatAUD(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(amount);
}

// THE R&D-share derivation line, split so callers can bold the percent. Shared by this card and
// the dashboard payroll provenance panel — one string builder so the two surfaces can never
// disagree on the derivation wording. Returns null when the ratio cannot be computed (Rule Zero:
// no fabricated percent).
export function rdShareParts(
  computed: ApportionmentView['computed'],
): { prefix: string; percentText: string } | null {
  if (computed.percent == null) return null;
  if (computed.basis === 'labour_hours' && computed.labourHours) {
    return {
      prefix: `R&D hours ${fmtHours(computed.labourHours.rdHours)} ÷ total hours ${fmtHours(computed.labourHours.totalHours)} = `,
      percentText: `${computed.percent}%`,
    };
  }
  return {
    prefix: `R&D labour ${formatAUD(computed.rdLabour)} ÷ loaded payroll ${formatAUD(computed.payrollTotal)} = `,
    percentText: `${computed.percent}%`,
  };
}

export const MODE_META: Record<ApportionmentView['mode'], { label: string; blurb: string }> = {
  manual: {
    label: 'Manual',
    blurb: 'No pre-fill. Enter an R&D percent for every overhead cost as you approve it.',
  },
  computed: {
    label: 'Computed',
    blurb: 'Pre-fill overhead costs from the R&D-labour-vs-payroll ratio below, so overhead tracks the claim’s own labour mix.',
  },
  custom: {
    label: 'Custom',
    blurb: 'Use your own justified percent (e.g. a board-approved split). The computed ratio is still shown for reference.',
  },
};

// Inline (mid-sentence) claim-mode labels. Keys MUST match the server ClaimMode ids
// (labour-calc.ts) — the derivation line looks up computed.claimMode here, so a stale key silently
// falls through to the raw id (e.g. "annual_salary_cap"). Copy mirrors the claims-UI mode toggle
// (store/claim-mode-store.ts CLAIM_MODE_META), lower-cased for mid-sentence use.
export const CLAIM_MODE_LABELS: Record<string, string> = {
  business_hours: 'business hours',
  business_hours_gross: 'business hours (gross)',
  all_project_hours: 'all project hours',
  classified_rd: 'classified R&D',
  manual_timesheet: 'manual timesheets',
  annual_salary_cap: 'annual salary ceiling',
  apportionment: 'apportionment (adopted shares)',
};

/**
 * Overhead apportionment methodology card. Renders the resolved ApportionmentView: the mode
 * selector, the payroll-derived derivation line (verbatim from data — never a fabricated figure),
 * and the custom percent + reason inputs. When the reconciliation ratio cannot be computed
 * (no Xero payroll synced) it renders an explicit cannot-compute state with no pre-fill (Rule Zero).
 */
export function MethodologyCard({
  incomeYear,
  view,
  isLoading,
  error,
  onRetry,
  onSaved,
  onApplied,
}: {
  incomeYear: string;
  view: ApportionmentView | undefined;
  isLoading: boolean;
  error: boolean;
  onRetry: () => void;
  onSaved: (next: ApportionmentView) => void;
  // Called after the labour-hours percent is applied to FY lines, so the page refetches totals.
  onApplied?: () => void;
}) {
  const [customPercent, setCustomPercent] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ApplyLabourPctResult | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Dry run first (real before/after totals, nothing written), then an explicit confirm.
  async function runApply(scope: ApplyLabourPctResult['scope'], dryRun: boolean) {
    setApplying(true);
    setApplyError(null);
    try {
      const res = await customInstance<{ data: ApplyLabourPctResult }>({
        url: '/api/expenditure/apportionment/apply-labour-pct',
        method: 'POST',
        data: { incomeYear, scope, dryRun },
      });
      if (dryRun) setPreview(res.data);
      else { setPreview(null); onApplied?.(); }
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setApplyError(msg ?? 'Failed to apply the labour-hours percent. Please try again.');
    } finally {
      setApplying(false);
    }
  }

  async function save(mode: ApportionmentView['mode']) {
    setSaving(true);
    setSaveError(null);
    try {
      // For custom, prefer the freshly-typed inputs; fall back to the stored view values so a bare
      // re-select of "custom" without edits re-submits the existing justified percent.
      const pctRaw = customPercent !== '' ? parseFloat(customPercent) : view?.customPercent ?? null;
      const reason = customReason.trim() !== '' ? customReason.trim() : view?.customReason ?? '';

      if (mode === 'custom') {
        if (pctRaw == null || !Number.isFinite(pctRaw) || pctRaw < 0 || pctRaw > 100) {
          setSaveError('Enter a percent between 0 and 100.');
          setSaving(false);
          return;
        }
        if (!reason) {
          setSaveError('A reason is required for a custom percent.');
          setSaving(false);
          return;
        }
      }

      const body: { mode: ApportionmentView['mode']; customPercent?: number; customReason?: string } =
        mode === 'custom' ? { mode, customPercent: pctRaw as number, customReason: reason } : { mode };

      const res = await customInstance<{ data: ApportionmentView }>({
        url: '/api/expenditure/apportionment',
        method: 'PUT',
        params: { incomeYear },
        data: body,
      });
      setCustomPercent('');
      setCustomReason('');
      onSaved(res.data);
    } catch {
      setSaveError('Failed to save the apportionment method. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-5 space-y-3">
        <div className="h-5 w-56 rounded skeleton" />
        <div className="h-16 w-full rounded skeleton" />
      </div>
    );
  }

  if (error || !view) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>Failed to load the overhead apportionment method.</span>
        <button onClick={onRetry} className="ml-auto underline text-xs">Retry</button>
      </div>
    );
  }

  const { computed, mode } = view;
  const derivation = rdShareParts(computed);
  const canCompute = derivation != null;
  const claimModeLabel = CLAIM_MODE_LABELS[computed.claimMode] ?? computed.claimMode;
  const isHoursBasis = computed.basis === 'labour_hours';

  return (
    <div className="rounded-lg border bg-card p-5 space-y-4">
      <div className="flex items-start gap-2">
        <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
        <div>
          <h2 className="font-medium text-sm">{isHoursBasis ? 'Non-labour apportionment method' : 'Overhead apportionment method'}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isHoursBasis
              ? 'How non-labour costs are turned into an R&D-claimable share. Computed = R&D hours ÷ total hours, from the claim’s own labour basis.'
              : 'How overhead costs (admin, subscriptions, insurance) are turned into an R&D-claimable share.'}
          </p>
        </div>
      </div>

      {/* Mode selector */}
      <div className="flex flex-wrap gap-2">
        {(Object.keys(MODE_META) as Array<ApportionmentView['mode']>).map((m) => {
          const active = mode === m;
          return (
            <button
              key={m}
              type="button"
              onClick={() => { if (!active) void save(m); }}
              disabled={saving}
              aria-pressed={active}
              className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background hover:bg-muted'
              }`}
            >
              {MODE_META[m].label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        {isHoursBasis && mode === 'computed'
          ? 'Non-labour lines are apportioned at R&D hours ÷ total hours. Apply it to the year’s lines below; lines on the labour % are re-stamped automatically at lock.'
          : MODE_META[mode].blurb}
      </p>

      {/* Derivation line — verbatim from the reconciliation figures, or an explicit cannot-compute
          state when payroll hasn't synced (Rule Zero: never fabricate a percent). */}
      {derivation && isHoursBasis ? (
        <div className="space-y-2">
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs font-mono text-foreground/90">
            {derivation.prefix}
            <span className="font-semibold">{derivation.percentText}</span> · {computed.incomeYear} · claim mode: {claimModeLabel}
            {computed.payrollPercent != null && (
              <span className="text-muted-foreground"> · cross-check: R&amp;D labour {formatAUD(computed.rdLabour)} ÷ Xero payroll {formatAUD(computed.payrollTotal)} = {computed.payrollPercent}%</span>
            )}
          </div>
          {computed.labourHours && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Hours by person</summary>
              <table className="mt-2 w-full max-w-lg text-left">
                <thead className="text-muted-foreground">
                  <tr><th className="font-medium py-1">Person</th><th className="font-medium py-1 text-right">Total hours</th><th className="font-medium py-1 text-right">R&amp;D hours</th><th className="font-medium py-1 text-right">%</th></tr>
                </thead>
                <tbody className="font-mono">
                  {computed.labourHours.people.map((p) => (
                    <tr key={p.userId} className="border-t">
                      <td className="py-1 font-sans">{p.name}{p.employmentType === 'casual' ? ' (casual, paid hours)' : ''}</td>
                      <td className="py-1 text-right">{fmtHours(p.standardHours)}</td>
                      <td className="py-1 text-right">{fmtHours(p.rdHours)}</td>
                      <td className="py-1 text-right">{p.standardHours > 0 ? ((p.rdHours / p.standardHours) * 100).toFixed(2) : '—'}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-muted-foreground">
                Total hours = rate-card annual hours × months employed (casual staff: hours paid). R&amp;D hours = total hours × the person&rsquo;s apportioned R&amp;D share, the same basis as the claim timesheet.
                {computed.labourHours.excludedUnrated.length > 0 && ` Not included (no rate card): ${computed.labourHours.excludedUnrated.map((u) => u.name).join(', ')}.`}
              </p>
            </details>
          )}
          {mode === 'computed' && view.lineStatus && (
            <LineStatusPanel
              status={view.lineStatus}
              percent={computed.percent as number}
              preview={preview}
              applying={applying}
              onPreview={(scope) => void runApply(scope, true)}
              onConfirm={() => preview && void runApply(preview.scope, false)}
              onCancel={() => setPreview(null)}
            />
          )}
          {applyError && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" aria-hidden="true" /> {applyError}
            </p>
          )}
        </div>
      ) : derivation ? (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs font-mono text-foreground/90">
          {derivation.prefix}
          <span className="font-semibold">{derivation.percentText}</span> · {computed.incomeYear} · claim mode:{' '}
          {claimModeLabel} · Xero synced {computed.monthsCovered}/12 months
        </div>
      ) : isHoursBasis ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 px-3 py-2 flex items-start gap-2">
          <TriangleAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="text-xs text-amber-800 dark:text-amber-300">
            <p className="font-medium">Cannot compute the labour-hours percent</p>
            <p className="text-amber-700 dark:text-amber-400 mt-0.5">
              {computed.labourHours && computed.labourHours.missingAnnualHours.length > 0
                ? `No annual hours on the rate card for ${computed.labourHours.missingAnnualHours.map((u) => u.name).join(', ')}. Add them in Rates.`
                : 'The claim labour figure is not available yet. Check that every project with effort has an adopted R&D share.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 px-3 py-2 flex items-start gap-2">
          <TriangleAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="text-xs text-amber-800 dark:text-amber-300">
            <p className="font-medium">Cannot compute — sync Xero payroll first</p>
            <p className="text-amber-700 dark:text-amber-400 mt-0.5">
              The R&D-labour-vs-payroll ratio needs synced payroll for {incomeYear}. Until then, computed mode has
              no percent to pre-fill and overhead costs must be apportioned manually.
            </p>
          </div>
        </div>
      )}

      {/* Custom mode: percent + reason inputs, and the custom-vs-computed comparison line. */}
      {mode === 'custom' && (
        <div className="space-y-3 border-t pt-4">
          {view.customPercent != null && (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">custom {view.customPercent}%</span>
              {' · '}
              computed {canCompute ? `${computed.percent}%` : '— (cannot compute)'}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr] gap-3 items-start">
            <div>
              <label className="text-xs font-medium block mb-1" htmlFor="apportionment-custom-pct">
                Custom percent <span className="text-destructive">*</span>
              </label>
              <input
                id="apportionment-custom-pct"
                type="number"
                min="0"
                max="100"
                step="0.1"
                inputMode="decimal"
                value={customPercent}
                onChange={(e) => setCustomPercent(e.target.value)}
                placeholder={view.customPercent != null ? String(view.customPercent) : 'e.g. 40'}
                className="w-full rounded border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1" htmlFor="apportionment-custom-reason">
                Reason / basis <span className="text-destructive">*</span>
              </label>
              <input
                id="apportionment-custom-reason"
                type="text"
                maxLength={2000}
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder={view.customReason ?? 'e.g. Board-approved 40% R&D split for FY2026'}
                className="w-full rounded border bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <Button variant="primary" size="sm" onClick={() => void save('custom')} disabled={saving}>
            <RotateCcw className={`h-3.5 w-3.5 ${saving ? 'animate-spin' : ''}`} aria-hidden="true" />
            {saving ? 'Saving…' : 'Save custom percent'}
          </Button>
        </div>
      )}

      {saveError && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" aria-hidden="true" /> {saveError}
        </p>
      )}
    </div>
  );
}

function LineStatusPanel({
  status, percent, preview, applying, onPreview, onConfirm, onCancel,
}: {
  status: LabourPctLineStatus;
  percent: number;
  preview: ApplyLabourPctResult | null;
  applying: boolean;
  onPreview: (scope: ApplyLabourPctResult['scope']) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const staleLines = status.stale.reduce((s, x) => s + x.lines, 0);
  const inSync = status.claimableLines > 0 && status.onLabourPctCurrent === status.claimableLines;

  if (preview) {
    return (
      <div role="region" aria-label="Apply labour-hours percent preview" className="rounded-md border px-3 py-2 text-xs space-y-2">
        <p>
          Apply <span className="font-semibold">{preview.percent}%</span> to {preview.matched} {preview.scope === 'all_claimable' ? 'claimable' : 'labour-% '} line{preview.matched === 1 ? '' : 's'}:
          claimable {fmtAUD2(preview.totals.claimableBefore)} → <span className="font-semibold">{fmtAUD2(preview.totals.claimableAfter)}</span>
          {' '}({preview.totals.claimableDelta >= 0 ? '+' : ''}{fmtAUD2(preview.totals.claimableDelta)}) on {fmtAUD2(preview.totals.amount)} of spend.
        </p>
        <div className="flex gap-2">
          <Button variant="primary" size="sm" onClick={onConfirm} disabled={applying || preview.matched === 0}>
            {applying ? 'Applying…' : 'Confirm and apply'}
          </Button>
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={applying}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-md border px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-2 ${inSync ? '' : 'border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 text-amber-800 dark:text-amber-300'}`}>
      {inSync ? (
        <span className="flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" aria-hidden="true" />
          All {status.claimableLines} claimable lines are at {percent}%.
        </span>
      ) : (
        <span className="flex items-center gap-1.5">
          <TriangleAlert className="h-3.5 w-3.5 text-amber-600 shrink-0" aria-hidden="true" />
          {status.onLabourPctCurrent} of {status.claimableLines} claimable lines at {percent}%
          {staleLines > 0 && `; ${staleLines} at an older labour % (${status.stale.map((x) => `${x.percent}%`).join(', ')})`}
          {status.notOnLabourPct > 0 && `; ${status.notOnLabourPct} not on the labour %`}.
          {' '}Lines on the labour % are re-stamped automatically when the claim is locked.
        </span>
      )}
      <div className="flex gap-2 ml-auto">
        {status.notOnLabourPct > 0 && (
          <Button variant="secondary" size="sm" onClick={() => onPreview('all_claimable')} disabled={applying}>
            Apply {percent}% to all claimable lines
          </Button>
        )}
        {staleLines > 0 && (
          <Button variant="secondary" size="sm" onClick={() => onPreview('labour_pct_only')} disabled={applying}>
            Re-sync labour % lines
          </Button>
        )}
      </div>
    </div>
  );
}
